/**
 * Shared Crew Actions - Copy/Paste functionality for planners
 *
 * EF-PLANNER-SHARED-CREW-ACTIONS v1.0
 * EVENTSFLO-CREW-PLANNER-COPY-PASTE-SHIFT-CREW-V1 — over-capacity paste, full
 * breakdown feedback, richer clipboard label.
 *
 * Single source of truth for crew clipboard operations used by:
 * - ShiftPlannerView (SHIFT Crew Planner)
 * - SheetCrewPlannerPage (SHEET Crew Planner)
 *
 * The paste path uses the existing `addShiftCrew` (POST /api/shifts/{id}/crew)
 * — same endpoint manual assignment and drag/drop already go through, so the
 * server-side deterministic rule pipeline (Crew Chief Supplement, Meal
 * Allowance, totals) re-runs exactly the same way.
 */

import { addShiftCrew, extractErrorMessage } from "../../lib/api";
import { toast } from "sonner";

/**
 * Clipboard structure:
 * {
 *   sourceShiftId: string,
 *   sourceLabel:   string,   // "Louis Vuitton LOGE / Crew / 2026-05-31"
 *   crew: Array<{ staff_id: string, staff_name: string, role: string }>
 * }
 */

const _fmtShiftLabel = (shift) => {
  if (!shift) return "(unknown shift)";
  const parts = [];
  if (shift.project_name) parts.push(shift.project_name);
  if (shift.name) parts.push(shift.name);
  if (shift.date) parts.push(String(shift.date).slice(0, 10));
  return parts.length ? parts.join(" / ") : `Shift ${String(shift.id || "").slice(0, 8)}`;
};

/**
 * Copy crew from a shift to clipboard.
 *
 * @param {Object} params
 * @param {string} params.shiftId         - Source shift ID
 * @param {Object} params.shiftCrewMap    - Map of shiftId -> crew[]
 * @param {Function} params.setCrewClipboard - State setter for clipboard
 * @param {Array}  [params.shifts]        - Optional shifts array, used to build
 *                                          a friendly source label for the toast.
 */
export function handleCopyCrew({ shiftId, shiftCrewMap, setCrewClipboard, shifts }) {
  const crew = shiftCrewMap[shiftId] || [];
  if (crew.length === 0) {
    toast.error("No crew to copy");
    return;
  }

  const sourceShift = Array.isArray(shifts) ? shifts.find((s) => s.id === shiftId) : null;
  const sourceLabel = _fmtShiftLabel(sourceShift);

  const crewData = crew.map((c) => ({
    staff_id: c.staff_id,
    staff_name: c.staff_name,
    role: c.role,
  }));

  setCrewClipboard({
    sourceShiftId: shiftId,
    sourceLabel,
    crew: crewData,
  });

  toast.success(`Copied ${crew.length} crew from ${sourceLabel}`);
}

/**
 * Paste crew from clipboard to a target shift.
 *
 * EVENTSFLO-CREW-PLANNER-COPY-PASTE-SHIFT-CREW-V1:
 *   - All non-duplicate crew are added, even if that pushes the shift past
 *     `staff_required` (over-capacity). The expanded panel renders those rows
 *     with warning styling but they remain interactive / removable.
 *   - Duplicate-by-staff-id is filtered client-side BEFORE the API calls so we
 *     never inflate count or hit a 409 storm.
 *
 * EVENTSFLO-CREW-PLANNER-ASSIGNMENT-OVERRIDE-FOR-INCOMPLETE-ONBOARDING-V1:
 *   - The first pass calls `addShiftCrew(...)` WITHOUT any override flag, so
 *     the existing deterministic backend rules (active / missing docs /
 *     expired docs / not onboarded) keep firing exactly as they do today.
 *   - The backend rejects each blocked crew with HTTP 403
 *     `{ error: "crew_not_active", can_override: true, reason, staff_name }`.
 *   - We collect those into `pendingOverride` and return them to the caller
 *     instead of toasting. The page-level `onPasteCrew` then shows a single
 *     batch override dialog. On admin confirm, the caller invokes
 *     `commitPasteOverride` which retries those crew with
 *     `onboarding_override=true`. Backend then records the override marker
 *     on each assignment row; onboarding/compliance truth is unchanged.
 *
 * Returns:
 *   {
 *     status: "done" | "needs_override",
 *     added, alreadyAssigned, excessCount, staffRequired,
 *     failedReasons: string[],   // non-overridable
 *     overridable: [{ staff_id, staff_name, role, reason }, ...],
 *     summarySoFar: string,
 *     toastId: number | string,  // already-loading toast — caller dismisses
 *   }
 */
export async function handlePasteCrew({
  targetShiftId,
  crewClipboard,
  shifts,
  shiftCrewMap,
  loadData,
}) {
  if (!crewClipboard || !crewClipboard.crew?.length) {
    toast.error("No crew in clipboard");
    return { status: "done" };
  }

  if (crewClipboard.sourceShiftId === targetShiftId) {
    toast.error("Cannot paste to the same shift");
    return { status: "done" };
  }

  const targetShift = (shifts || []).find((s) => s.id === targetShiftId);
  const staffRequired = targetShift?.staff_required || 0;

  const existingCrew = shiftCrewMap[targetShiftId] || [];
  const existingStaffIds = new Set(existingCrew.map((c) => c.staff_id));
  const currentCount = existingCrew.length;

  // Skip anyone already on the target shift.
  const crewToAdd = crewClipboard.crew.filter(
    (c) => !existingStaffIds.has(c.staff_id)
  );
  const alreadyAssigned = crewClipboard.crew.length - crewToAdd.length;

  if (crewToAdd.length === 0) {
    toast.info(`All ${crewClipboard.crew.length} copied crew member(s) are already on this shift.`);
    return { status: "done", added: 0, alreadyAssigned };
  }

  let excessCount = 0;
  if (staffRequired > 0) {
    const projectedAfter = currentCount + crewToAdd.length;
    if (projectedAfter > staffRequired) {
      excessCount = projectedAfter - staffRequired;
    }
  }

  const toastId = toast.loading(`Pasting ${crewToAdd.length} crew…`);
  const failedReasons = [];
  const overridable = [];
  let addedCount = 0;

  for (const crewMember of crewToAdd) {
    try {
      await addShiftCrew(
        targetShiftId,
        crewMember.staff_id,
        crewMember.role || "Crew",
      );
      addedCount++;
    } catch (err) {
      // EVENTSFLO-CREW-PLANNER-ASSIGNMENT-OVERRIDE-FOR-INCOMPLETE-ONBOARDING-V1
      // Recognise the 403 `crew_not_active` payload that the backend uses to
      // signal "would be assignable if you pass onboarding_override=true".
      // Anything else (404, conflict 409, tenant 403, server 500, network
      // error, etc.) goes into `failedReasons` and is not retried.
      const data = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 403 && data && typeof data === "object" && data.error === "crew_not_active" && data.can_override) {
        overridable.push({
          staff_id: crewMember.staff_id,
          staff_name: data.crew_name || data.staff_name || crewMember.staff_name || crewMember.staff_id,
          role: crewMember.role || "Crew",
          reason: data.reason || (typeof data.message === "string" ? data.message : "Missing required documents"),
        });
      } else {
        const reason = extractErrorMessage(err) || "Unknown error";
        failedReasons.push(`${crewMember.staff_name || crewMember.staff_id}: ${reason}`);
      }
    }
  }

  const summarySoFar =
    `Added ${addedCount}` +
    (alreadyAssigned > 0 ? ` · ${alreadyAssigned} already assigned` : "") +
    (excessCount > 0 ? ` · ${excessCount} excess (over capacity ${staffRequired})` : "") +
    (failedReasons.length > 0 ? ` · ${failedReasons.length} failed` : "");

  if (overridable.length > 0) {
    // Caller will show the batch-override dialog. Keep the loading toast
    // visible until commitPasteOverride finishes.
    return {
      status: "needs_override",
      added: addedCount,
      alreadyAssigned,
      excessCount,
      staffRequired,
      failedReasons,
      overridable,
      summarySoFar,
      toastId,
    };
  }

  // No override needed — finalise normally.
  try { await loadData(); } catch { /* non-fatal */ }

  if (addedCount > 0 && failedReasons.length === 0) {
    toast.success(summarySoFar, { id: toastId });
  } else if (addedCount > 0 && failedReasons.length > 0) {
    toast.warning(`${summarySoFar}\nFailures: ${failedReasons.slice(0, 3).join("; ")}`, {
      id: toastId,
      duration: 8000,
    });
  } else {
    toast.error(`Paste failed. ${failedReasons.slice(0, 3).join("; ") || "No crew added."}`, {
      id: toastId,
      duration: 8000,
    });
  }
  return {
    status: "done",
    added: addedCount,
    alreadyAssigned,
    excessCount,
    staffRequired,
    failedReasons,
  };
}

/**
 * EVENTSFLO-CREW-PLANNER-ASSIGNMENT-OVERRIDE-FOR-INCOMPLETE-ONBOARDING-V1
 *
 * Second pass — admin has confirmed the batch override dialog. Retry each
 * overridable crew member with `onboarding_override=true` so the backend
 * persists the assignment AND the `onboarding_override` marker on the
 * `shift_crew_assignments` row (the row carries override-by / override-at
 * via the backend's existing logger + audit trail; onboarding/compliance
 * truth on the crew profile is NOT touched).
 *
 * Inputs are the previous return value from handlePasteCrew plus the target
 * shift id and the planner's loadData() reload function.
 */
export async function commitPasteOverride({
  targetShiftId,
  pending, // result object returned by handlePasteCrew with status="needs_override"
  loadData,
}) {
  if (!pending || !pending.overridable?.length) return;

  let addedCount = pending.added || 0;
  let overrideAdded = 0;
  const failedReasons = [...(pending.failedReasons || [])];

  for (const c of pending.overridable) {
    try {
      // addShiftCrew(shiftId, staffId, role, notes, overrideConflicts, conflictOverrideReason, onboardingOverride)
      await addShiftCrew(
        targetShiftId,
        c.staff_id,
        c.role || "Crew",
        null,
        false,
        null,
        true, // onboarding_override
      );
      addedCount++;
      overrideAdded++;
    } catch (err) {
      const reason = extractErrorMessage(err) || "Unknown error";
      failedReasons.push(`${c.staff_name || c.staff_id}: ${reason}`);
    }
  }

  try { await loadData(); } catch { /* non-fatal */ }

  const summary =
    `Added ${addedCount}` +
    (overrideAdded > 0 ? ` (${overrideAdded} via override)` : "") +
    (pending.alreadyAssigned > 0 ? ` · ${pending.alreadyAssigned} already assigned` : "") +
    (pending.excessCount > 0 ? ` · ${pending.excessCount} excess (over capacity ${pending.staffRequired})` : "") +
    (failedReasons.length > 0 ? ` · ${failedReasons.length} failed` : "");

  if (failedReasons.length === 0) {
    toast.success(summary, { id: pending.toastId });
  } else if (addedCount > 0) {
    toast.warning(`${summary}\nFailures: ${failedReasons.slice(0, 3).join("; ")}`, {
      id: pending.toastId,
      duration: 9000,
    });
  } else {
    toast.error(`Paste failed. ${failedReasons.slice(0, 3).join("; ") || "No crew added."}`, {
      id: pending.toastId,
      duration: 9000,
    });
  }
}
